//External
import { useMemo } from "react";
//Internal
import { countMatchingVehicles } from "./availabilityPool";

/*******************
@Purpose : given every other already-active filter, work out which
           candidate options of one facet would show 0 vehicles if picked on
           their own - so those options can be hidden entirely instead of
           shown as dead ends. Computed instantly off the shared
           availability pool (one fetch per location+dates, done once at the
           page level) instead of firing a network request per option.
@Parameter : {pool, filterKey, options, otherFilters, colorList}
  - pool: vehicles already available for the current location+dates (see
    useAvailabilityPool) - date matching itself isn't redone here
  - filterKey: the facet key this checks (e.g. "categorySlugs", "seats")
  - options: [{ key, values }] - `key` identifies the option (returned in
    the result set), `values` is what gets checked for that one option on
    its own (a single-item array for most facets, the full expanded seat
    list for a "X or more" seat tier, etc.)
  - otherFilters: every other already-active filter's current values,
    merged in as context so the isolation check reflects combined filtering
  - colorList: needed to translate a colorIds otherFilter/option (real,
    encrypted ids) into names for matching - see availabilityPool.js
@Author : INIC
******************/
export default function useUnavailableOptions({ pool, filterKey, options, otherFilters = {}, colorList }) {
  return useMemo(() => {
    if (!pool?.length || !options?.length) return new Set();

    const unavailable = new Set();
    options.forEach(({ key, values }) => {
      const count = countMatchingVehicles(pool, { ...otherFilters, [filterKey]: values }, colorList);
      if (count === 0) unavailable.add(key);
    });
    return unavailable;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool, filterKey, JSON.stringify(options), JSON.stringify(otherFilters), colorList]);
}
